import { formatDateToScheduleFields, parseScheduleFieldsToIso } from './scheduleTimezone';

const DAY_MONTH_YEAR_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const HOUR_MINUTE_REGEX = /^(\d{2}):(\d{2})$/;

/**
 * Converts publicationDateTime (ISO) to pt-BR publishDate / publishTime fields.
 */
export function publicationDateTimeToScheduleFields(publicationDateTime) {
  return formatDateToScheduleFields(publicationDateTime);
}

export function isValidScheduleDateField(publishDate = '') {
  return DAY_MONTH_YEAR_REGEX.test(String(publishDate).trim());
}

export function isValidScheduleTimeField(publishTime = '') {
  return HOUR_MINUTE_REGEX.test(String(publishTime).trim());
}

export function isCompleteScheduleDateTimeFields(publishDate = '', publishTime = '') {
  return isValidScheduleDateField(publishDate) && isValidScheduleTimeField(publishTime);
}

export function isArtScheduleCommitted(art = {}) {
  return Boolean(
    art.publicationId &&
    String(art.scheduleStatus || '').toLowerCase() === 'scheduled' &&
    art.publicationDateTime,
  );
}

export function getCanonicalScheduleFields(art = {}) {
  if (isArtScheduleCommitted(art)) {
    return publicationDateTimeToScheduleFields(art.publicationDateTime);
  }

  return {
    publishDate: art.publishDate || '',
    publishTime: art.publishTime || '',
  };
}

export function scheduleFieldsDifferFromCanonical(edited = {}, art = {}) {
  if (!isArtScheduleCommitted(art)) {
    return false;
  }

  const canonical = publicationDateTimeToScheduleFields(art.publicationDateTime);
  const editedDate = String(edited.publishDate || '').trim();
  const editedTime = String(edited.publishTime || '').trim();

  if (!editedDate && !editedTime) {
    return false;
  }

  return (
    editedDate !== canonical.publishDate || editedTime !== canonical.publishTime
  );
}

export function getArtCaption(art = {}) {
  const candidates = [art?.legend, art?.legenda, art?.caption, art?.postCaption];
  const found = candidates.find(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
  return found ? found.trim() : '';
}

/**
 * Builds schedule form fields from an art object (strategy chain or session medias).
 */
export function buildScheduleFieldsFromArt(art = {}) {
  const caption = getArtCaption(art);
  const canonical = getCanonicalScheduleFields(art);

  if (isArtScheduleCommitted(art)) {
    return {
      caption,
      publishDate: canonical.publishDate,
      publishTime: canonical.publishTime,
      selectedNetworks: art.socialMedias || art.selectedNetworks || [],
      publicationStatus: art.publicationStatus || '',
      publicationDateTime: art.publicationDateTime,
      publicationId: art.publicationId,
    };
  }

  if (art.publishDate || art.publishTime) {
    return {
      caption,
      publishDate: art.publishDate || '',
      publishTime: art.publishTime || '',
      selectedNetworks: art.socialMedias || art.selectedNetworks || [],
      publicationStatus: art.publicationStatus || '',
    };
  }

  const fromDateTime = publicationDateTimeToScheduleFields(art.publicationDateTime);

  return {
    caption,
    publishDate: fromDateTime.publishDate,
    publishTime: fromDateTime.publishTime,
    selectedNetworks: art.socialMedias || art.selectedNetworks || [],
    publicationStatus: art.publicationStatus || '',
  };
}

/**
 * Resolves publishDate/publishTime for finalize, falling back to publicationDateTime.
 */
export function resolveScheduleDateTimeFields(art = {}, editedData = {}) {
  if (
    isCompleteScheduleDateTimeFields(
      editedData.publishDate,
      editedData.publishTime,
    )
  ) {
    return {
      publishDate: String(editedData.publishDate).trim(),
      publishTime: String(editedData.publishTime).trim(),
    };
  }

  const publishDate = editedData.publishDate || art.publishDate || '';
  const publishTime = editedData.publishTime || art.publishTime || '';

  if (publishDate || publishTime) {
    return { publishDate, publishTime };
  }

  return publicationDateTimeToScheduleFields(art.publicationDateTime);
}

export function mergeScheduleArtsWithSession(widgetArts = [], sessionDetail = null) {
  const scheduledArts = sessionDetail?.userInput?.scheduledArts || [];
  const sessionMedias = Array.isArray(sessionDetail?.medias)
    ? sessionDetail.medias
    : [];

  return (widgetArts || []).map((art, index) => {
    const pieceNumber = art.piece_number ?? art.pieceNumber ?? index + 1;
    const scheduled = scheduledArts.find((item, scheduledIndex) => {
      const scheduledPiece =
        item?.piece_number ?? item?.pieceNumber ?? scheduledIndex + 1;
      return Number(scheduledPiece) === Number(pieceNumber);
    });
    const media = sessionMedias.find(
      (item) => Number(item?.pieceNumber) === Number(pieceNumber),
    );

    return {
      ...art,
      ...scheduled,
      ...media,
      piece_number: pieceNumber,
      pieceNumber,
      imageUrl: art.imageUrl || art.url || media?.url,
      url: art.url || art.imageUrl || media?.url,
      legend: art.legend || art.legenda || media?.legenda || scheduled?.legend,
      publishDate:
        scheduled?.publishDate || art.publishDate || media?.publishDate || '',
      publishTime:
        scheduled?.publishTime || art.publishTime || media?.publishTime || '',
      publicationDateTime:
        scheduled?.publicationDateTime ||
        media?.publicationDateTime ||
        art.publicationDateTime,
      publicationId:
        scheduled?.publicationId || media?.publicationId || art.publicationId,
      scheduleStatus:
        scheduled?.scheduleStatus || media?.scheduleStatus || art.scheduleStatus,
      publicationStatus:
        media?.publicationStatus || art.publicationStatus,
      socialMedias:
        scheduled?.socialMedias || art.socialMedias || media?.socialMedias,
    };
  });
}

export function buildInitialScheduleEntryFromArt(art = {}) {
  const fields = buildScheduleFieldsFromArt(art);
  return {
    publishDate: fields.publishDate,
    publishTime: fields.publishTime,
    caption: fields.caption,
    originalCaption: fields.caption,
    selectedNetworks: fields.selectedNetworks,
  };
}

export function getArtScheduleKey(art, index = 0) {
  return art?.piece_number ?? art?.pieceNumber ?? index + 1;
}

export function buildSchedulePatchFromFields(publishDate = '', publishTime = '') {
  if (!isCompleteScheduleDateTimeFields(publishDate, publishTime)) {
    return null;
  }

  const publicationDateTime = parseScheduleFieldsToIso(publishDate, publishTime);
  if (!publicationDateTime) {
    return null;
  }

  return {
    publishDate: String(publishDate).trim(),
    publishTime: String(publishTime).trim(),
    publicationDateTime,
    scheduledAt: publicationDateTime,
  };
}

export function mergeArtsWithScheduleData(arts = [], artScheduleData = {}) {
  return (arts || []).map((art, index) => {
    const artKey = getArtScheduleKey(art, index);
    const editedData = artScheduleData[artKey] || {};
    const { publishDate, publishTime } = resolveScheduleDateTimeFields(
      art,
      editedData,
    );
    const schedulePatch = buildSchedulePatchFromFields(publishDate, publishTime);

    if (!schedulePatch) {
      return art;
    }

    return {
      ...art,
      ...schedulePatch,
    };
  });
}
